import React, { useEffect, useState } from 'react';
import { ProcessoFacade, Processo, ProcessoDTO } from '../../facades/ProcessoFacade';
import { ClienteFacade, Cliente } from '../../facades/ClienteFacade';
import Sidebar from '../Sidebar'; // Importando o novo Sidebar (Caminho corrigido)

export const ProcessosList: React.FC = () => {
    // Estados
    const [processos, setProcessos] = useState<Processo[]>([]);
    const [clientesCombo, setClientesCombo] = useState<Cliente[]>([]); // Para o Dropdown
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState<string | null>(null);
    const [sucesso, setSucesso] = useState<string | null>(null);

    // Modal Form
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editandoId, setEditandoId] = useState<number | null>(null);
    const [formData, setFormData] = useState<ProcessoDTO>({
        numeroCnj: '', assunto: '', status: 'Ativo', dataAutuacao: '', valorCausa: 0, clienteId: 0
    });
    const [salvando, setSalvando] = useState(false);

    // Modal Exclusão
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null as number | null, cnj: '' });

    // Efeito UX: Auto-close alertas
    useEffect(() => {
        if (sucesso || erro) {
            const timer = setTimeout(() => { setSucesso(null); setErro(null); }, 5000);
            return () => clearTimeout(timer);
        }
    }, [sucesso, erro]);

    // Carregamento Inicial
    useEffect(() => {
        carregarDadosBase();
    }, []);

    const carregarDadosBase = async () => {
        try {
            setLoading(true);
            const [processosData, clientesData] = await Promise.all([
                ProcessoFacade.listarTodos(),
                ClienteFacade.listarTodos()
            ]);
            setProcessos(processosData || []);
            setClientesCombo(clientesData || []);
        } catch (error) {
            setProcessos([]);
            setClientesCombo([]);
            setErro("Sessão expirada ou acesso negado. Por favor, faça login novamente.");
        } finally {
            setLoading(false);
        }
    };

    // ==========================================
    // FORMATAÇÕES UX (MÁSCARA E MOEDA)
    // ==========================================
    const mascaraCNJ = (valor: string | undefined) => {
        if (!valor) return "";
        let v = valor.replace(/\D/g, "");
        v = v.substring(0, 20);
        v = v.replace(/^(\d{7})(\d)/, "$1-$2");
        v = v.replace(/-(\d{2})(\d)/, "-$1.$2");
        v = v.replace(/\.(\d{4})(\d)/, ".$1.$2");
        v = v.replace(/\.(\d{1})(\d)/, ".$1.$2");
        v = v.replace(/\.(\d{2})(\d)/, ".$1.$2");
        return v;
    };

    const formatarMoeda = (valor: number) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
    };

    // ==========================================
    // AÇÕES DO CRUD
    // ==========================================
    const abrirModalNovo = () => {
        setErro(null); setSucesso(null);
        setFormData({ numeroCnj: '', assunto: '', status: 'Ativo', dataAutuacao: '', valorCausa: 0, clienteId: 0 });
        setEditandoId(null);
        setIsModalOpen(true);
    };

    const abrirModalEditar = (proc: Processo) => {
        setErro(null); setSucesso(null);
        setFormData({
            numeroCnj: mascaraCNJ(proc.numeroCnj),
            assunto: proc.assunto,
            status: proc.status,
            dataAutuacao: proc.dataAutuacao,
            valorCausa: proc.valorCausa,
            clienteId: proc.clienteId // CORRIGIDO
        });
        setEditandoId(proc.id);
        setIsModalOpen(true);
    };

    const handleSalvar = async (e: React.FormEvent) => {
        e.preventDefault();
        setErro(null); setSucesso(null);

        if (formData.clienteId === 0) {
            setErro("Por favor, selecione um Cliente (Órgão) para o processo.");
            return;
        }

        const dadosLimpos: ProcessoDTO = {
            ...formData,
            numeroCnj: formData.numeroCnj.replace(/\D/g, "")
        };

        try {
            setSalvando(true);
            if (editandoId) {
                await ProcessoFacade.atualizar(editandoId, dadosLimpos);
                setSucesso("Processo atualizado com sucesso!");
            } else {
                await ProcessoFacade.criar(dadosLimpos);
                setSucesso("Processo autuado com sucesso!");
            }
            setIsModalOpen(false);
            await carregarDadosBase();
        } catch (error: any) {
            setErro("Erro ao salvar. Verifique se este CNJ já não está cadastrado em outro processo.");
        } finally {
            setSalvando(false);
        }
    };

    const confirmarExclusao = async () => {
        if (!deleteModal.id) return;
        try {
            await ProcessoFacade.excluir(deleteModal.id);
            setSucesso(`Processo excluído com sucesso!`);
            setDeleteModal({ isOpen: false, id: null, cnj: '' });
            await carregarDadosBase();
        } catch (error) {
            setErro("Erro ao excluir o processo.");
            setDeleteModal({ isOpen: false, id: null, cnj: '' });
        }
    };

    return (
        <Sidebar>
            <header className="mb-8 border-b-2 border-gray-200 pb-4 flex justify-between items-end flex-wrap gap-4">
                <div>
                    <h2 className="text-3xl font-extrabold text-gray-800 tracking-tight">Gestão de Processos (NPU)</h2>
                    <p className="text-gray-500 font-medium mt-1">Acompanhe e cadastre as ações judiciais vinculadas aos órgãos.</p>
                </div>
                <button onClick={abrirModalNovo} className="bg-blue-800 hover:bg-blue-900 text-white font-bold py-2 px-6 rounded-lg shadow-lg transition-all flex items-center gap-2 border border-blue-700 whitespace-nowrap">
                    <span>⚖️</span> Autuar Processo
                </button>
            </header>

            {/* AREA DE ALERTS */}
            <div className="min-h-[70px]">
                {erro && (
                    <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-lg mb-6 flex items-center justify-between animate-fade-in"><div className="flex items-center gap-3"><span className="text-2xl">⚠️</span><p className="font-semibold">{erro}</p></div><button onClick={() => setErro(null)} className="text-xl font-bold">&times;</button></div>
                )}
                {sucesso && (
                    <div className="bg-green-50 border-l-4 border-green-500 text-green-800 p-4 rounded-lg mb-6 flex items-center justify-between animate-fade-in"><div className="flex items-center gap-3"><span className="text-2xl">✅</span><p className="font-semibold">{sucesso}</p></div><button onClick={() => setSucesso(null)} className="text-xl font-bold">&times;</button></div>
                )}
            </div>

            {/* TABELA DE PROCESSOS */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-20"><div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-900 mb-4"></div><p className="text-xl text-blue-900 font-bold animate-pulse">Sincronizando processos...</p></div>
            ) : (
                <div className="bg-white rounded-2xl shadow-lg overflow-x-auto border border-gray-100">
                    <table className="min-w-full text-left text-sm whitespace-nowrap">
                        <thead className="uppercase tracking-wider border-b-2 border-gray-200 bg-slate-50 text-gray-600 font-black">
                        <tr>
                            <th className="px-6 py-4">NPU (Número Único)</th>
                            <th className="px-6 py-4">Órgão / Cliente</th>
                            <th className="px-6 py-4">Assunto</th>
                            <th className="px-6 py-4">Status</th>
                            <th className="px-6 py-4">Valor</th>
                            <th className="px-6 py-4 text-center">Ações</th>
                        </tr>
                        </thead>
                        <tbody>
                        {!Array.isArray(processos) || processos.length === 0 ? (
                            <tr><td colSpan={6} className="text-center py-8 text-gray-500 italic">Nenhum processo localizado.</td></tr>
                        ) : (
                            processos.map((proc) => (
                                <tr key={proc.id} className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                                    <td className="px-6 py-4 font-mono font-bold text-blue-900">{mascaraCNJ(proc.numeroCnj)}</td>
                                    <td className="px-6 py-4 font-bold text-gray-700">🏢 {proc.clienteNome}</td>
                                    <td className="px-6 py-4 text-gray-600 max-w-[200px] truncate" title={proc.assunto}>{proc.assunto}</td>
                                    <td className="px-6 py-4">
                                            <span className={`py-1 px-3 rounded-full text-[10px] font-black uppercase ${proc.status === 'Ativo' ? 'bg-green-100 text-green-700' : proc.status === 'Julgado' ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-700'}`}>
                                                {proc.status}
                                            </span>
                                    </td>
                                    <td className="px-6 py-4 font-semibold text-gray-700">{formatarMoeda(proc.valorCausa)}</td>
                                    <td className="px-6 py-4 text-center">
                                        <button onClick={() => abrirModalEditar(proc)} className="text-blue-600 hover:text-blue-900 font-bold mr-4">Editar</button>
                                        <button onClick={() => setDeleteModal({ isOpen: true, id: proc.id, cnj: mascaraCNJ(proc.numeroCnj) })} className="text-red-500 hover:text-red-700 font-bold">Excluir</button>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* MODAL DE CADASTRO/EDIÇÃO */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden my-8">
                        <div className="bg-blue-900 p-5 flex justify-between items-center border-b border-yellow-400">
                            <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                {editandoId ? '✏️ Atualizar Processo' : '⚖️ Autuar Novo Processo'}
                            </h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-blue-200 hover:text-white font-bold text-2xl leading-none">&times;</button>
                        </div>

                        <form onSubmit={handleSalvar} className="p-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                <div className="col-span-1 sm:col-span-2">
                                    <label className="block text-gray-700 font-bold mb-2 text-sm">Cliente Vinculado (Órgão Público)</label>
                                    <select
                                        required
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-900 font-semibold"
                                        value={formData.clienteId}
                                        onChange={(e) => setFormData({...formData, clienteId: Number(e.target.value)})}
                                    >
                                        <option value="0" disabled>-- Selecione um Cliente --</option>
                                        {Array.isArray(clientesCombo) && clientesCombo.map(c => (
                                            <option key={c.id} value={c.id}>{c.nome} (CNPJ: {c.cpfCnpj})</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-span-1 md:col-span-1">
                                    <label className="block text-gray-700 font-bold mb-2 text-sm">Número NPU (CNJ)</label>
                                    <input
                                        type="text" required maxLength={25}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-900 font-mono tracking-wider"
                                        value={formData.numeroCnj}
                                        onChange={(e) => setFormData({...formData, numeroCnj: mascaraCNJ(e.target.value)})}
                                        placeholder="0000000-00.0000.0.00.0000"
                                    />
                                </div>

                                <div className="col-span-1 md:col-span-1">
                                    <label className="block text-gray-700 font-bold mb-2 text-sm">Status do Processo</label>
                                    <select
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-900"
                                        value={formData.status}
                                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                                    >
                                        <option value="Ativo">🟢 Ativo</option>
                                        <option value="Suspenso">🟡 Suspenso</option>
                                        <option value="Julgado">🔵 Julgado</option>
                                        <option value="Arquivado">⚪ Arquivado</option>
                                    </select>
                                </div>

                                <div className="col-span-1 sm:col-span-2">
                                    <label className="block text-gray-700 font-bold mb-2 text-sm">Assunto Principal</label>
                                    <input type="text" required className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-900" value={formData.assunto} onChange={(e) => setFormData({...formData, assunto: e.target.value})} placeholder="Ex: Mandado de Segurança Coletivo" />
                                </div>

                                <div className="col-span-1 md:col-span-1">
                                    <label className="block text-gray-700 font-bold mb-2 text-sm">Data de Autuação</label>
                                    <input type="date" required className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-900 text-gray-600" value={formData.dataAutuacao} onChange={(e) => setFormData({...formData, dataAutuacao: e.target.value})} />
                                </div>

                                <div className="col-span-1 md:col-span-1">
                                    <label className="block text-gray-700 font-bold mb-2 text-sm">Valor da Causa (R$)</label>
                                    <input type="number" step="0.01" min="0" required className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-900" value={formData.valorCausa || ''} onChange={(e) => setFormData({...formData, valorCausa: parseFloat(e.target.value)})} placeholder="0.00" />
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 mt-4">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-5 py-2.5 text-gray-600 font-bold rounded-lg hover:bg-gray-100 transition-colors">Cancelar</button>
                                <button type="submit" disabled={salvando} className="px-6 py-2.5 bg-blue-900 hover:bg-blue-800 text-white font-bold rounded-lg shadow-md transition-all disabled:opacity-50">
                                    {salvando ? '⏳ Autuando...' : '💾 Salvar Processo'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL DE EXCLUSÃO */}
            {deleteModal.isOpen && (
                <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden text-center p-6">
                        <h3 className="text-2xl font-black text-gray-800 mb-2">Excluir Processo</h3>
                        <p className="text-gray-600 mb-6">Deseja realmente remover o NPU <br/><strong className="text-gray-900">{deleteModal.cnj}</strong>?</p>
                        <div className="flex flex-col sm:flex-row-reverse gap-3">
                            <button onClick={confirmarExclusao} className="w-full sm:w-auto rounded-lg bg-red-600 px-6 py-3 font-bold text-white hover:bg-red-700 transition-all">Sim, excluir</button>
                            <button onClick={() => setDeleteModal({ isOpen: false, id: null, cnj: '' })} className="w-full sm:w-auto rounded-lg border border-gray-300 px-6 py-3 font-bold text-gray-700 hover:bg-gray-50 transition-all">Cancelar</button>
                        </div>
                    </div>
                </div>
            )}
        </Sidebar>
    );
};